import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';

/**
 * ValueBlock — one of the "Core Value Propositions" sections on a vertical
 * spoke page. Left column = title/blurb, right column = bullet list where
 * each row is `{ label, detail }`. `detail` can be JSX (for InlineLinks).
 *
 * Sub-groups: pass `bullets` as an array of `{ heading, items }` objects
 * (checked via `Array.isArray(bullets[0].items)`) to render nested groups.
 */
export default function ValueBlock({
  eyebrow,
  title,
  blurb,
  bullets,
  testId,
  reverse = false,
}) {
  const hasSubGroups = Array.isArray(bullets) && bullets[0] && Array.isArray(bullets[0].items);

  return (
    <PublicSection variant="default" testId={testId} className="!py-14 md:!py-20">
      <div className={`grid gap-10 lg:grid-cols-5 lg:gap-14 items-start ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <Reveal className="lg:col-span-2">
          <div>
            {eyebrow && (
              <div className="flex items-center gap-3 mb-4">
                <span aria-hidden className="h-4 w-1 bg-primary" />
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                  {eyebrow}
                </p>
              </div>
            )}
            <h2 className="text-3xl md:text-4xl font-heading font-black tracking-tight text-foreground leading-[1.05]">
              {title}
            </h2>
            {blurb && (
              <p className="mt-5 text-sm md:text-base text-muted-foreground leading-relaxed">
                {blurb}
              </p>
            )}
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.08}>
          {hasSubGroups ? (
            <div className="space-y-8">
              {bullets.map((group) => (
                <div key={group.heading}>
                  <h3 className="text-sm md:text-base font-heading font-bold text-foreground uppercase tracking-wide mb-3">
                    {group.heading}
                  </h3>
                  <ul className="space-y-4">
                    {group.items.map((b, i) => (
                      <BulletRow key={bulletKey(b, i)} label={b.label} detail={b.detail} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-4">
              {bullets.map((b, i) => (
                <BulletRow key={bulletKey(b, i)} label={b.label} detail={b.detail} />
              ))}
            </ul>
          )}
        </Reveal>
      </div>
    </PublicSection>
  );
}

function BulletRow({ label, detail }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2
        className="w-5 h-5 shrink-0 text-primary mt-0.5"
        strokeWidth={2.25}
        aria-hidden
      />
      <div>
        <p className="text-sm md:text-base font-semibold text-foreground">{label}</p>
        {detail && (
          <p className="mt-1 text-sm md:text-base text-muted-foreground leading-relaxed">
            {detail}
          </p>
        )}
      </div>
    </li>
  );
}

// Derive a stable React key for a bullet row. Prefers the label text
// (unique per list in practice) and falls back to the positional index
// when the label is JSX (e.g., contains an <InlineLink>). The bullet
// lists are static config that never reorder, so the fallback is safe.
function bulletKey(bullet, i) {
  return typeof bullet.label === 'string' ? bullet.label : `bullet-${i}`;
}
